import type { Ref } from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';

import type {
  IPaymentSystemCountriesResponse,
  IPaymentSystemCountry,
  IPaymentSystemQueryType,
} from '../types';
import {
  ADMIN_PAYMENT_SYSTEM_COUNTRIES_URL,
  AFFILIATE_CASHOUT_PAYMENT_SYSTEM_COUNTRIES_URL,
  AFFILIATE_PAYMENT_SYSTEM_COUNTRIES_URL,
  PAYMENT_SYSTEM_COUNTRIES_QUERY_KEY,
} from '../constants';

export default function usePaymentSystemsCountriesQuery(
  affId: Ref<number | undefined>,
  queryType: Ref<IPaymentSystemQueryType> = ref('default'),
  isDisabled?: boolean,
): IContextQueryResult<IPaymentSystemCountriesResponse, Error, IPaymentSystemCountry[]> {
  const { isAdminNamespace } = useCurrentNamespace();
  const { fetchGet } = useRestClient();

  return useContextQuery<IPaymentSystemCountriesResponse, Error, IPaymentSystemCountry[]>({
    queryKey: [PAYMENT_SYSTEM_COUNTRIES_QUERY_KEY, affId, queryType],
    queryFn: async ({ signal }) => {
      let url: string;
      if (isAdminNamespace.value) {
        url = ADMIN_PAYMENT_SYSTEM_COUNTRIES_URL;
      } else if (queryType.value === 'cashout') {
        url = AFFILIATE_CASHOUT_PAYMENT_SYSTEM_COUNTRIES_URL;
      } else {
        url = AFFILIATE_PAYMENT_SYSTEM_COUNTRIES_URL;
      }

      return fetchGet<IPaymentSystemCountriesResponse>(url, {
        signal,
        urlParams: {
          id: affId.value,
        },
      });
    },
    select: (data) => data.countries,
    enabled: computed(() => !isDisabled && (!isAdminNamespace.value || !!affId.value)),
  });
}
